<template>
  <div>
    <div>
      <div
        v-show="
          title || filterBy || filterable || $slots.filter || $slots.beforeHeaderLeft || $slots.afterHeaderLeft || $slots.beforeHeaderRight || $slots.afterHeaderRight
        "
        class="mb-2 flex items-center justify-between"
      >
        <div class="flex items-center space-x-4">
          <slot name="beforeHeaderLeft" />
          <h4 v-if="title" class="flex w-full font-medium">
            {{ title }}
          </h4>
          <slot name="filter">
            <FguTableFilter v-if="filterBy" :name="filterBy" :options="filterOptions" @change="(value) => (filter = value)" />
          </slot>
          <slot name="afterHeaderLeft" />
        </div>
        <div class="flex items-center space-x-2">
          <slot name="beforeHeaderRight" />
          <SearchField
            v-if="filterable"
            id="fgu-table-search"
            name="fgu-table-search"
            placeholder="Find i liste"
            :modelValue="query"
            @input="($event) => (query = $event.target.value)"
          />
          <slot name="afterHeaderRight" />
        </div>
      </div>
      <div ref="tableContainer" class="relative overflow-auto rounded-md border bg-white" :style="tableContainerStyle">
        <div
          v-if="selectionActive && $slots.selectionHeader"
          class="absolute inset-x-0 top-0 z-20 flex items-center border-b border-gray-200 bg-gray-100 px-4 py-2 text-xs tracking-wider"
          :class="{ 'sticky top-0': maxRows }"
        >
          <slot name="selectionHeader" />
        </div>
        <table ref="table" class="fgu-table" :class="{ 'fgu-table--sticky-header': maxRows }">
          <thead :class="{ invisible: selectionActive && $slots.selectionHeader }">
            <tr>
              <th v-if="draggable" class="w-10" data-testid="drag-handle-column"></th>
              <th v-if="$slots.prependedColumn && $slots.h__prependedColumn">
                <slot
                  name="h__prependedColumn"
                  v-bind="{
                    sortRows: sortRows,
                    orderDirection: orderDirection,
                  }"
                />
              </th>
              <th v-for="column in activeColumns" :key="column">
                <slot
                  v-if="!hiddenHeaderColumns.includes(column)"
                  :name="'h__' + column"
                  v-bind="{
                    heading: getHeading(column),
                    column,
                    sortRows: sortRows,
                    orderDirection: orderDirection,
                    isCurrentSort: localSortBy.includes(column),
                  }"
                >
                  <div v-if="filterColumns.includes(column) || copyColumns.includes(column)" class="flex items-center">
                    <button
                      v-if="!disableSorting && !draggable"
                      type="button"
                      class="flex items-center uppercase"
                      :class="columnClasses['h__' + column]"
                      @click="sortRows(column)"
                    >
                      <span>{{ getHeading(column) }}</span>
                      <svgicon v-if="localSortBy.includes(column) && orderDirection" :name="sortIcon" size="sm" class="ml-1" />
                    </button>
                    <span v-else class="uppercase" :class="columnClasses['h__' + column]">{{ getHeading(column) }}</span>
                    <FguTableColumnFilter
                      v-if="filterColumns.includes(column)"
                      :heading="getHeading(column)"
                      :options="columnFilterOptions[column] || []"
                      :modelValue="columnFilters[column] ?? []"
                      @update:modelValue="setColumnFilter(column, $event)"
                    />
                    <BaseIconButton
                      v-if="copyColumns.includes(column)"
                      v-tooltip="`Kopiér ${getHeading(column)}`"
                      icon="clipboard-copy"
                      size="sm"
                      tighten
                      :aria-label="`Kopiér ${getHeading(column)}`"
                      @click.stop="copyColumnValues(column)"
                    />
                  </div>
                  <template v-else>
                    <button v-if="!disableSorting && !draggable" type="button" :class="columnClasses['h__' + column]" @click="sortRows(column)">
                      <span>{{ getHeading(column) }}</span>
                      <svgicon v-if="localSortBy.includes(column) && orderDirection" :name="sortIcon" size="sm" class="ml-1" />
                    </button>
                    <span v-else :class="columnClasses['h__' + column]">{{ getHeading(column) }}</span>
                  </template>
                </slot>
              </th>

              <th>
                <button type="button"></button>
              </th>

              <th>
                <ButtonGroup inTableRow>
                  <DropdownContextMenu size="md" color="gray-400" :options="{ appendTo: 'body' }">
                    <DropdownItemGroup>
                      <DropdownItem
                        icon="table"
                        @click="
                          $openModal({
                            name: 'FguTableColumnsModal',
                            props: { allColumns: columns, activeColumns, headings, id },
                          })
                        "
                      >
                        Tilpas kolonner
                      </DropdownItem>
                      <DropdownItem v-if="!disableExport" icon="download" @click="downloadTableData"> Download </DropdownItem>
                    </DropdownItemGroup>
                  </DropdownContextMenu>
                </ButtonGroup>
              </th>
            </tr>
          </thead>
          <tbody v-if="loading">
            <tr v-for="row in 3" :key="row">
              <td v-for="column in columnsCount - 1" :key="column" class="animate-pulse" :colspan="[column === columnsCount - 1 ? 2 : null]" :tabindex="-1">
                <span class="flex">
                  <span class="block h-4 w-3/4 rounded-sm bg-gray-200" />
                </span>
              </td>
            </tr>
          </tbody>
          <tbody v-else-if="filteredSearchedAndSortedRows.length < 1">
            <tr>
              <td :class="`col-span-${columnsCount + 1}`" :colspan="columnsCount + 1">
                <p class="py-3 text-center text-gray-400">{{ emptyText }}</p>
              </td>
            </tr>
          </tbody>
          <SlickList
            v-else-if="draggable"
            :list="filteredSearchedAndSortedRows"
            tag="tbody"
            :useDragHandle="true"
            lockAxis="y"
            class="select-none"
            data-testid="draggable-tbody"
            @sortEnd="handleReorder"
          >
            <SlickItem
              v-for="(row, rowIndex) in filteredSearchedAndSortedRows"
              :id="`fgu-table-row-${getRowId(row)}`"
              :key="getRowId(row)"
              :index="rowIndex"
              tag="tr"
              :class="[
                clickableRow && 'cursor-pointer',
                activeRowId && activeRowId === row.id && 'active',
                row?.isDeleted && 'deleted',
                alternatingRowColors && rowIndex % 2 === 1 && 'bg-gray-50',
                rowClasses && rowClasses(row),
              ]"
              @click="$emit('rowClick', { row, rowIndex })"
            >
              <td class="w-10 text-gray-400" tabindex="-1">
                <span class="flex items-center">
                  <svgicon v-handle name="grip-vertical" size="sm" class="cursor-grab" data-testid="drag-handle" />
                </span>
              </td>
              <td v-if="$slots.prependedColumn">
                <span class="flex items-center">
                  <slot name="prependedColumn" v-bind="{ row, rowIndex }" />
                </span>
              </td>
              <td v-for="column in activeColumns" :key="column" tabindex="-1" colspan="1" :width="columnWidths[column]">
                <span class="flex items-center" :class="columnClasses[column]">
                  <slot :name="column" v-bind="{ row, rowIndex }">
                    <FguTableCellValue :value="get(row, column)" :limit="truncationLimit(column)" />
                  </slot>
                </span>
              </td>
              <td tabindex="-1" :colspan="[!$slots.appendedColumn ? 2 : 1]"></td>
              <td v-if="$slots.appendedColumn" :width="columnWidths.appendedColumn">
                <span class="flex items-center">
                  <slot name="appendedColumn" v-bind="{ row, rowIndex, isOpen: activeChildRowId === getRowId(row) }" />
                </span>
              </td>
            </SlickItem>
          </SlickList>
          <tbody v-for="(orderedRows, name) in groupedRows(false)" v-else :key="name" :data-testid="name">
            <tr v-if="name !== '__default'" data-utility-row>
              <td
                class="fgu-table__group-name bg-gray-100 text-gray-700"
                :class="`col-span-${$slots.appendedColumn ? columnsCount + 2 : columnsCount + 1}`"
                :colspan="columnsCount + [$slots.prependedColumn, $slots.appendedColumn].filter(Boolean).length + 1"
              >
                <slot name="groupName" v-bind="{ rows: orderedRows, name }">
                  <span class="flex">{{ name }}</span>
                </slot>
              </td>
            </tr>
            <FguTableRow
              v-for="(row, rowIndex) in orderedRows"
              :id="`fgu-table-row-${row.id || rowIndex}`"
              :key="getRowId(row)"
              :class="[
                clickableRow && 'cursor-pointer',
                activeRowId && activeRowId === row.id && 'active',
                row?.isDeleted && 'deleted',
                alternatingRowColors && rowIndex % 2 === 1 && 'bg-gray-50',
                rowClasses && rowClasses(row),
              ]"
              @click="$emit('rowClick', { row, rowIndex })"
            >
              <template #default>
                <td v-if="$slots.prependedColumn">
                  <span class="flex items-center">
                    <slot name="prependedColumn" v-bind="{ row, rowIndex }" />
                  </span>
                </td>

                <td v-for="column in activeColumns" :key="column" tabindex="-1" colspan="1" :width="columnWidths[column]">
                  <router-link v-if="route" class="flex items-center" :class="columnClasses[column]" :to="route(row)" tabindex="-1">
                    <slot :name="column" v-bind="{ row, rowIndex }">
                      <FguTableCellValue :value="get(row, column)" :limit="truncationLimit(column)" />
                    </slot>
                  </router-link>
                  <slot v-else :name="column" v-bind="{ row, rowIndex }">
                    <span class="flex items-center" :class="columnClasses[column]">
                      <FguTableCellValue :value="get(row, column)" :limit="truncationLimit(column)" />
                    </span>
                  </slot>
                </td>

                <td tabindex="-1" :colspan="[!$slots.appendedColumn ? 2 : 1]"></td>

                <td v-if="$slots.appendedColumn" :width="columnWidths.appendedColumn">
                  <a
                    v-if="route"
                    class="flex items-center"
                    :href="$router.resolve(route(row)).href"
                    tabindex="-1"
                    @click="
                      ($event) => {
                        $event.preventDefault();
                        $router.push(route(row));
                      }
                    "
                  >
                    <slot name="appendedColumn" v-bind="{ row, rowIndex, isOpen: activeChildRowId === getRowId(row) }" />
                  </a>
                  <span v-else class="flex items-center">
                    <slot name="appendedColumn" v-bind="{ row, rowIndex, isOpen: activeChildRowId === getRowId(row) }" />
                  </span>
                </td>
              </template>
              <template v-if="activeChildRowId === getRowId(row)" #childRow>
                <td :colspan="columnsCount" :class="`col-span-${columnsCount}`">
                  <slot name="childRow" v-bind="{ row, rowIndex }" />
                </td>
              </template>
            </FguTableRow>
            <slot
              name="appendedGroupRow"
              v-bind="{
                rows: orderedRows,
                activeColumns,
              }"
            />
          </tbody>
          <tbody data-testid="appended-row" class="appended-row">
            <slot
              name="appendedRow"
              v-bind="{
                rows: filteredSearchedAndSortedRows,
                activeColumns,
                getHeading,
              }"
            />
          </tbody>
        </table>
      </div>
      <Pagination v-if="showPagination" :total="filteredSearchedAndSortedRows.length" :currentPage="paginationPage" :perPage="perPage" @change="paginationPage = $event" />
      <div v-else-if="!hideResultsCount && filteredSearchedAndSortedRows.length" class="space-between mt-2 flex w-full items-center">
        <p class="text-sm text-gray-700">Viser {{ resultsCountText }}</p>
      </div>
    </div>
    <div
      v-if="renderVirtual"
      ref="virtualWrapper"
      style="position: absolute; left: -99999px; top: -99999px; width: 1px; height: 1px; overflow: hidden; visibility: hidden"
      aria-hidden="true"
    >
      <table ref="virtualTable">
        <thead>
          <tr>
            <th v-for="column in activeColumns" :key="column">
              <slot
                v-if="!hiddenHeaderColumns.includes(column)"
                :name="'h__' + column"
                v-bind="{
                  heading: getHeading(column),
                  column,
                  sortRows: sortRows,
                  orderDirection: orderDirection,
                  isCurrentSort: localSortBy.includes(column),
                }"
              >
                {{ getHeading(column) }}
              </slot>
            </th>
          </tr>
        </thead>
        <tbody v-if="filteredSearchedAndSortedRows.length < 1">
          <tr>
            <td :colspan="columnsCount">{{ emptyText }}</td>
          </tr>
        </tbody>
        <tbody v-for="(orderedRows, name) in groupedRows(true)" v-else :key="name">
          <FguTableRow v-for="(row, rowIndex) in orderedRows" :id="`virtual-fgu-table-row-${row.id || rowIndex}`" :key="row.id || rowIndex">
            <template #default>
              <td
                v-for="(column, columnIndex) in activeColumns"
                :key="column"
                tabindex="-1"
                :colspan="[columnIndex === activeColumns.length - 1 && !$slots.appendedColumn ? 2 : 1]"
              >
                <slot :name="column" v-bind="{ row, rowIndex }">
                  {{ get(row, column) }}
                </slot>
              </td>
            </template>
            <template v-if="activeChildRowId === getRowId(row)" #childRow>
              <td :colspan="columnsCount">
                <slot name="childRow" v-bind="{ row, rowIndex }" />
              </td>
            </template>
          </FguTableRow>
          <slot
            name="appendedGroupRow"
            v-bind="{
              rows: orderedRows,
              activeColumns,
            }"
          />
        </tbody>
        <tbody data-testid="virtual-appended-row">
          <slot
            name="appendedRow"
            v-bind="{
              rows: filteredSearchedAndSortedRows,
              activeColumns,
              getHeading,
            }"
          />
        </tbody>
      </table>
    </div>
  </div>
</template>

<script>
import clone from "lodash/clone";
import get from "lodash/get";
import groupBy from "lodash/groupBy";
import orderBy from "lodash/orderBy";
import { HandleDirective, SlickItem, SlickList } from "vue-slicksort";
import ButtonGroup from "~/components/button-group/ButtonGroup";
import { fguTableEvents } from "~/components/fgu-table/FguTableEvents";
import Pagination from "~/components/paginations/Pagination";
import formatCpr from "~/filters/formatCpr";
import formatDate from "~/filters/formatDate";
import formatDateTime from "~/filters/formatDateTime";
import formatPhoneNumber from "~/filters/formatPhoneNumber";
import formatTime from "~/filters/formatTime";
import { withRenderContextMixin } from "~/mixins/withRenderContextMixin";
import { copyToClipboard } from "~/utils/clipboard";
import { dayjs } from "~/utils/dayjs";
import { downloadCsv } from "~/utils/downloadCsv";
import { pluralize } from "~/utils/string";
import DropdownContextMenu from "../dropdown/DropdownContextMenu";
import DropdownItem from "../dropdown/DropdownItem";
import DropdownItemGroup from "../dropdown/DropdownItemGroup";
import SearchField from "../search-field/SearchField";
import FguTableCellValue from "./FguTableCellValue";
import FguTableColumnFilter from "./FguTableColumnFilter";
import FguTableFilter from "./FguTableFilter";
import FguTableRow from "./FguTableRow";

export const LOCAL_STORAGE_HIDDEN_COLUMNS_KEY = "FGU_TABLE_HIDDEN_COLUMNS";
export const LOCAL_STORAGE_SORT_BY_KEY = "FGU_TABLE_SORT_BY";
export const LOCAL_STORAGE_ORDER_KEY = "FGU_TABLE_ORDER";
export const DEFAULT_TRUNCATE_LIMIT = 80;

export default {
  name: "FguTable",

  components: {
    DropdownContextMenu,
    DropdownItemGroup,
    DropdownItem,
    ButtonGroup,
    SearchField,
    FguTableCellValue,
    FguTableColumnFilter,
    FguTableFilter,
    FguTableRow,
    Pagination,
    SlickList,
    SlickItem,
  },

  directives: { handle: HandleDirective },

  mixins: [withRenderContextMixin("table")],

  props: {
    storageKey: {
      type: String,
      default: null,
    },

    columns: {
      type: Array,
      required: true,
    },

    rows: {
      type: Array,
      required: true,
    },

    headings: {
      type: Object,
      default: () => ({}),
    },

    emptyText: {
      type: String,
      default: "Ingen resultater",
    },

    customSorting: {
      type: Object,
      default: () => ({}),
    },

    customDownloadColumns: {
      type: Object,
      default: () => ({}),
    },

    customFiltering: {
      type: Object,
      default: () => ({}),
    },

    customFormatting: {
      type: Object,
      default: () => ({}),
    },

    columnClasses: {
      type: Object,
      default: () => ({}),
    },

    columnWidths: {
      type: Object,
      default: () => ({}),
    },

    hiddenColumns: {
      type: Array,
      default: () => [],
    },

    hiddenHeaderColumns: {
      type: Array,
      default: () => [],
    },

    hiddenDownloadColumns: {
      type: Array,
      default: () => [],
    },

    integerColumns: {
      type: Array,
      default: () => [],
    },

    dateColumns: {
      type: Array,
      default: () => [],
    },

    cprColumns: {
      type: Array,
      default: () => [],
    },

    datetimeColumns: {
      type: Array,
      default: () => [],
    },

    phoneNumberColumns: {
      type: Array,
      default: () => [],
    },

    timeColumns: {
      type: Array,
      default: () => [],
    },

    groupBy: {
      type: Array,
      default: () => [],
    },

    sortBy: {
      type: [String, Array],
      default: () => [],
    },

    order: {
      type: [String, Array],
      default: () => [],
    },

    uniqueColumn: {
      type: [String, Function],
      default: "id",
    },

    id: {
      type: String,
      default: "",
    },

    perPage: Number,
    title: String,
    route: Function,
    loading: Boolean,
    filterable: Boolean,
    disableExport: Boolean,
    disableSorting: Boolean,
    hideResultsCount: Boolean,
    filterBy: String,
    activeRowId: String,
    clickableRow: Boolean,
    selectionActive: Boolean,
    alternatingRowColors: Boolean,

    filterColumns: {
      type: Array,
      default: () => [],
    },

    // Columns whose header copies the shown rows' values, comma-separated (e.g. emails for a mail to everyone listed).
    copyColumns: {
      type: Array,
      default: () => [],
    },

    truncatedColumns: {
      type: Object,
      default: () => ({}),
    },

    rowClasses: Function,
    maxRows: {
      type: Number,
      default: null,
    },

    draggable: {
      type: Boolean,
      default: false,
    },
  },

  emits: ["rowClick", "reorder"],

  data() {
    const persistedHiddenColumns = this.getPersistedHiddenColumns();

    return {
      localRows: clone(this.rows),
      localSortBy: this.getSortBy(),
      localOrder: this.getOrder(),
      localHiddenColumns: persistedHiddenColumns || this.hiddenColumns,
      query: "",
      filter: null,
      activeChildRowId: null,
      columnFilters: {},
      paginationPage: 1,
      renderVirtual: false,
      truncationActive: false,
    };
  },

  computed: {
    activeColumns() {
      return this.columns.filter((column) => !this.localHiddenColumns.includes(column));
    },

    hasTruncatedColumns() {
      return Object.keys(this.truncatedColumns).length > 0;
    },

    resultsCountText() {
      return pluralize(this.filteredSearchedAndSortedRows.length, "resultat");
    },

    injectedCells() {
      return Object.keys(this.$slots).filter((slotName) => this.columns.includes(slotName));
    },

    columnsCount() {
      return this.activeColumns.length + 1;
    },

    showPagination() {
      return this.filteredSearchedAndSortedRows.length > this.perPage;
    },

    filteredRows() {
      if (!this.filterBy || !this.filter) {
        return this.localRows;
      }

      return this.localRows.filter((row) => get(row, this.filterBy) === this.filter);
    },

    columnFilterOptions() {
      const options = {};
      const activeFilters = Object.entries(this.columnFilters).filter(([, v]) => v.length > 0);
      for (const col of this.filterColumns) {
        const otherFilters = activeFilters.filter(([c]) => c !== col);
        const rows = otherFilters.length ? this.filteredRows.filter((row) => otherFilters.every(([c, vals]) => vals.includes(get(row, c)))) : this.filteredRows;
        const seen = new Set();
        const values = [];
        for (const row of rows) {
          const v = get(row, col);
          if (v == null || v === "") continue;
          if (seen.has(v)) continue;
          seen.add(v);
          values.push(v);
        }
        values.sort((a, b) => String(a).localeCompare(String(b), "da"));
        options[col] = values;
      }
      return options;
    },

    columnFilteredRows() {
      const active = Object.entries(this.columnFilters).filter(([, v]) => v.length > 0);
      if (!active.length) return this.filteredRows;
      return this.filteredRows.filter((row) => active.every(([col, vals]) => vals.includes(get(row, col))));
    },

    filteredAndSearchedRows() {
      if (!this.query) {
        return this.columnFilteredRows;
      }

      const queryLowerCase = this.query.toLowerCase();

      return this.columnFilteredRows.filter((row) =>
        this.columns.some((column) => {
          if (this.customFiltering[column]) {
            return this.customFiltering[column](row, this.query);
          }

          return this.get(row, column)?.toString().toLowerCase().includes(queryLowerCase) || false;
        })
      );
    },

    filteredSearchedAndSortedRows() {
      const { filteredAndSearchedRows, customSorting, cprColumns, integerColumns, dateColumns, datetimeColumns, localSortBy, localOrder } = this;

      if (this.draggable) {
        return filteredAndSearchedRows;
      }

      const sortMethods = localSortBy.map((column) => {
        if (customSorting[column]) {
          return customSorting[column];
        }

        if (integerColumns.includes(column)) {
          return (row) => parseInt(get(row, column), 10);
        }

        if (dateColumns.includes(column)) {
          return (row) => {
            const value = get(row, column);
            return value ? dayjs(value).toDate() : new Date(0);
          };
        }

        if (datetimeColumns.includes(column)) {
          return (row) => {
            const value = get(row, column);
            return value ? dayjs(value).unix() : 0;
          };
        }

        if (cprColumns.includes(column)) {
          return (row) => {
            const value = get(row, column);
            if (!value) return [new Date(0), ""];
            return [dayjs(value.substring(0, 6), "DDMMYY").toDate(), value.slice(-4)];
          };
        }

        // orderBy is case-sensitive by default, so we need to convert strings to lowercase
        return (row) => {
          const value = column?.includes(".") ? get(row, column) : row[column];
          return typeof value === "string" ? value.toLowerCase() : value;
        };
      });

      if (sortMethods.length < 1) {
        return filteredAndSearchedRows;
      }

      return orderBy(filteredAndSearchedRows, sortMethods, localOrder);
    },

    paginatedRows() {
      if (!this.showPagination) return this.filteredSearchedAndSortedRows;

      const startIndex = this.paginationPage * this.perPage - this.perPage;
      const endIndex = startIndex + this.perPage;
      return this.filteredSearchedAndSortedRows.slice(startIndex, endIndex);
    },

    groupedRows() {
      return (isVirtual = false) => {
        if (!this.groupBy.length) {
          return {
            __default: isVirtual ? this.filteredSearchedAndSortedRows : this.paginatedRows,
          };
        }

        // group the rows using the raw data (using get() instead of this.get()) as you may have custom formatting that changes the value like date columns
        const grouped = groupBy(isVirtual ? this.filteredSearchedAndSortedRows : this.paginatedRows, (row) => this.groupBy.map((key) => get(row, key)).join(", "));

        // simply sort the keys to ensure the order stays consistent. always show true values first.
        const sortedKeys = Object.keys(grouped).sort((a, b) => {
          if (a === "true") {
            return a !== b ? -1 : 1;
          }
          if (a === "false") {
            return a !== b ? 1 : -1;
          }
          return a.localeCompare(b, "da");
        });

        // build the final object and apply custom formatting
        return sortedKeys.reduce((row, key) => {
          const customKey = this.groupBy.map((path) => this.get(grouped[key][0], path)).join(", ");

          row[customKey] = grouped[key];

          return row;
        }, {});
      };
    },

    filterOptions() {
      if (!this.filterBy) {
        return [];
      }

      const options = this.localRows
        .map((row) => get(row, this.filterBy))
        .filter((value, index, array) => value && array.indexOf(value) === index)
        .map((value) => ({
          label: value.toString(),
          value,
        }));

      return [{ label: "Alle", value: null }, ...options];
    },

    orderDirection() {
      if (this.localOrder.length !== 1) {
        return null;
      }

      return this.localOrder[0] === "desc" ? "descending" : "ascending";
    },

    sortIcon() {
      return this.orderDirection === "descending" ? "arrow-down-wide-narrow" : "arrow-up-narrow-wide";
    },

    tableContainerStyle() {
      if (!this.maxRows) return {};

      // Row height: py-2 (8px + 8px) + text line height (~21px) ≈ 41px
      // Header height: py-2 (8px + 8px) + text (~17px) ≈ 33px
      const rowHeight = 41;
      const headerHeight = 33;
      const maxHeight = headerHeight + this.maxRows * rowHeight;

      return { maxHeight: `${maxHeight}px` };
    },
  },

  watch: {
    rows: {
      deep: true,
      handler() {
        this.localRows = clone(this.rows);
      },
    },

    localSortBy: {
      deep: true,
      handler() {
        this.persistLocalSortBy();
      },
    },

    "activeColumns.length"() {
      this.measureOverflow();
    },

    filteredSearchedAndSortedRows() {
      this.measureOverflow();
    },

    localOrder: {
      deep: true,
      handler() {
        this.persistLocalOrder();
      },
    },
  },

  created() {
    fguTableEvents.on("toggle-column", this.toggleColumn);
  },

  mounted() {
    if (!this.hasTruncatedColumns) return;

    this.measureOverflow();

    // Kept off `data` so Vue does not proxy it — ResizeObserver methods throw
    // when invoked on a reactive proxy instead of the instance itself.
    this.containerObserver = new ResizeObserver(this.handleContainerResize);
    this.containerObserver.observe(this.$refs.tableContainer);
  },

  beforeUnmount() {
    fguTableEvents.off("toggle-column", this.toggleColumn);
    this.containerObserver?.disconnect();
  },

  methods: {
    truncationLimit(column) {
      if (!this.truncationActive || !(column in this.truncatedColumns)) return null;

      return this.truncatedColumns[column] || DEFAULT_TRUNCATE_LIMIT;
    },

    // Measuring is always done untruncated: truncation is what removes the
    // overflow, so reading the width while it is applied would report a table
    // that fits and untruncate itself back into overflowing.
    async measureOverflow() {
      if (!this.hasTruncatedColumns) return;

      this.truncationActive = false;
      await this.$nextTick();

      const { table, tableContainer } = this.$refs;
      if (!table || !tableContainer) return;

      this.truncationActive = table.scrollWidth > tableContainer.clientWidth;
    },

    handleContainerResize([entry]) {
      const { width } = entry.contentRect;

      // Truncating shrinks the table, never the container, so an unchanged
      // width means we are seeing our own truncation echoed back.
      if (width === this.observedContainerWidth) return;

      this.observedContainerWidth = width;
      this.measureOverflow();
    },

    get(row, path) {
      const value = get(row, path);

      if (this.customFormatting[path]) {
        return this.customFormatting[path](row);
      }

      if (typeof value === "boolean") {
        return value ? "✓" : "";
      }

      if (this.dateColumns.includes(path)) {
        return formatDate(value);
      }

      if (this.datetimeColumns.includes(path)) {
        return formatDateTime(value);
      }

      if (this.cprColumns.includes(path)) {
        return formatCpr(value);
      }

      if (this.phoneNumberColumns.includes(path)) {
        if (value) {
          return formatPhoneNumber(value);
        }
        return "-";
      }

      if (this.timeColumns.includes(path)) {
        if (value) {
          return formatTime(value);
        }
        return "";
      }

      return value;
    },

    getRowId(row) {
      if (typeof this.uniqueColumn === "function") {
        return this.uniqueColumn(row);
      } else {
        return this.get(row, this.uniqueColumn);
      }
    },

    toggleColumn({ column, id = "" }) {
      if (id !== this.id) return;
      if (this.localHiddenColumns.includes(column)) {
        this.localHiddenColumns = this.localHiddenColumns.filter((hiddenColumn) => hiddenColumn !== column);
      } else {
        this.localHiddenColumns.push(column);
      }

      this.persistLocalHiddenColumns();
    },

    toggleChildRow(row) {
      const id = this.getRowId(row);
      if (this.activeChildRowId === id) {
        this.activeChildRowId = null;
      } else {
        this.activeChildRowId = id;
      }
    },

    setColumnFilter(column, value) {
      this.columnFilters = { ...this.columnFilters, [column]: value };
      this.paginationPage = 1;
    },

    handleReorder({ oldIndex, newIndex }) {
      // SlickList leaves stray text selection on the drop target on some
      // browsers; clear it before doing anything else so the table doesn't
      // appear "all highlighted" after a drop.
      window.getSelection?.()?.removeAllRanges();

      if (oldIndex === newIndex) return;

      const reordered = [...this.filteredSearchedAndSortedRows];
      const [moved] = reordered.splice(oldIndex, 1);
      reordered.splice(newIndex, 0, moved);

      this.$emit("reorder", {
        orderedIds: reordered.map((row) => this.getRowId(row)),
        oldIndex,
        newIndex,
        movedId: this.getRowId(moved),
      });
    },

    sortRows(column) {
      if (this.localSortBy.length === 1 && this.localSortBy[0] === column) {
        if (this.localOrder.length < 1 || this.localOrder[0] === "asc") {
          this.localOrder = ["desc"];
        } else {
          this.localOrder = ["asc"];
        }
      } else {
        this.localSortBy = [column];
        this.localOrder = ["asc"];
      }
    },

    async downloadTableData() {
      this.renderVirtual = true;

      try {
        const name = document.title;

        // wait for Vue reactivity + child rendering. twice nextTick to be sure ;-)
        await this.$nextTick();
        await this.$nextTick();

        const rows = this.$refs.virtualTable.querySelectorAll("tr");
        const data = [];
        const dataRows = Object.values(this.groupedRows(true)).flat();
        let value = "";

        // Get groupBy column headings
        const groupByHeadings = this.groupBy.map((key) => this.headings[key] || key);

        for (let i = 0; i < rows.length; i++) {
          const cols = rows[i].querySelectorAll("td, th");
          const row = [];

          // Add groupBy columns at the beginning
          if (this.groupBy.length > 0) {
            if (i === 0) {
              // Header row: add groupBy column headings
              row.push(...groupByHeadings);
            } else {
              // Data row: add groupBy column values
              const dataRow = dataRows[i - 1];
              this.groupBy.forEach((key) => {
                const groupValue = this.get(dataRow, key) || "";
                row.push(typeof groupValue === "string" ? groupValue.trim() : groupValue);
              });
            }
          }

          for (let j = 0; j < cols.length; j++) {
            const col = cols[j];
            const key = this.activeColumns[j];
            const customMapping = this.customDownloadColumns[key];
            const colspan = parseInt(col.getAttribute("colspan") || "1", 10);

            if (i > 0 && customMapping) {
              if (typeof customMapping === "function") {
                value = customMapping(dataRows[i - 1]);
              } else {
                value = this.get(dataRows[i - 1], customMapping);
              }
            } else {
              const input = col.querySelector("input, textarea, select");
              const useInput = input && input.type !== "checkbox" && input.type !== "radio";

              if (useInput) {
                value = input.parentElement.textContent.trim() || input.value; // try to get the translated value
              } else {
                value = col.textContent;
              }
            }

            const stringValue = !value ? "" : String(value);

            if (Number.isNaN(parseFloat(stringValue))) {
              row.push(stringValue.trim());
            } else {
              // Excel uses comma as decimal separator for numbers, we also need to remove the thousand separators
              row.push(stringValue.replace(/\./g, "").replace(".", ","));
            }

            if (colspan > 1) {
              for (let cs = 1; cs < colspan; cs++) {
                row.push("");
              }
            }
          }

          data.push(row.join(";"));
        }

        downloadCsv(data.join("\n"), name);
      } finally {
        this.renderVirtual = false;
      }
    },

    getLocalStorageKey() {
      if (!this.storageKey) return this.getDefaultStorageKey();

      return this.storageKey;
    },

    getDefaultStorageKey() {
      return window.location.pathname + this.id;
    },

    persistLocalHiddenColumns() {
      const value = this.localHiddenColumns;
      const key = this.getLocalStorageKey();

      const object = JSON.parse(localStorage.getItem(LOCAL_STORAGE_HIDDEN_COLUMNS_KEY)) || {};
      object[key] = value;

      localStorage.setItem(LOCAL_STORAGE_HIDDEN_COLUMNS_KEY, JSON.stringify(object));
    },

    getPersistedHiddenColumns() {
      const key = this.getLocalStorageKey();
      const object = JSON.parse(localStorage.getItem(LOCAL_STORAGE_HIDDEN_COLUMNS_KEY)) || {};

      return object[key] || object[this.getDefaultStorageKey()];
    },

    persistLocalSortBy() {
      const value = this.localSortBy;
      const key = this.getLocalStorageKey();

      const object = JSON.parse(localStorage.getItem(LOCAL_STORAGE_SORT_BY_KEY)) || {};
      object[key] = value;

      localStorage.setItem(LOCAL_STORAGE_SORT_BY_KEY, JSON.stringify(object));
    },

    getSortBy() {
      const key = this.getLocalStorageKey();
      const object = JSON.parse(localStorage.getItem(LOCAL_STORAGE_SORT_BY_KEY)) || {};

      if (object[key]) {
        return object[key];
      }
      return Array.isArray(this.sortBy) ? this.sortBy : [this.sortBy];
    },

    persistLocalOrder() {
      const value = this.localOrder;
      const key = this.getLocalStorageKey();

      const object = JSON.parse(localStorage.getItem(LOCAL_STORAGE_ORDER_KEY)) || {};
      object[key] = value;

      localStorage.setItem(LOCAL_STORAGE_ORDER_KEY, JSON.stringify(object));
    },

    getOrder() {
      const key = this.getLocalStorageKey();
      const object = JSON.parse(localStorage.getItem(LOCAL_STORAGE_ORDER_KEY)) || {};

      if (object[key]) {
        return object[key];
      }
      return Array.isArray(this.order) ? this.order : [this.order];
    },

    async copyColumnValues(column) {
      const values = this.filteredSearchedAndSortedRows.map((row) => get(row, column)).filter((value) => value != null && `${value}`.trim() !== "");

      if (await copyToClipboard([...new Set(values)].join(", "))) {
        this.$toast.success(`${this.getHeading(column)}-celler kopieret`);
      } else {
        this.$toast.error("Kunne ikke kopiere");
      }
    },

    getHeading(column) {
      const heading = this.headings[column];

      if (heading === undefined) {
        return column;
      }

      return heading;
    },
  },
};
</script>

<style lang="postcss">
@reference "tailwindcss";
.fgu-table {
  @apply min-w-full;
  border-collapse: collapse;
}

.fgu-table tr:hover td:not(.fgu-table__group-name) {
  @apply bg-gray-50;
}

.fgu-table tr.active td:not(.fgu-table__group-name) {
  @apply bg-blue-50;
}

.fgu-table tr.deleted td:not(.fgu-table__group-name) {
  @apply opacity-50;
}

.fgu-table tr td {
  @apply border-b;
}

.fgu-table tbody ~ tbody .fgu-table__group-name {
  @apply border-t;
}

.fgu-table .appended-row tr:first-child td {
  @apply border-t;
}

.fgu-table tr:last-child td {
  @apply border-0;
}

.fgu-table th {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  text-align: left;
  outline: 0;
  padding: 0;
  @apply border-b border-gray-200 bg-gray-100 text-xs tracking-wider text-gray-400;
}

.fgu-table th > * {
  @apply flex w-full items-center space-x-1 px-4 py-1 font-medium uppercase focus:outline-hidden;
}

.fgu-table td {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  padding: 0;
  outline: 0;
  @apply text-sm text-gray-500;
}

.fgu-table td > span,
.fgu-table td > a {
  width: 100%;
  min-height: 100%;
  @apply px-4 py-2 align-middle;
}

.fgu-table .fgu-table__group-name > span {
  @apply py-2 leading-none;
}

.fgu-table td:first-child {
  @apply font-medium text-gray-900;
}

.fgu-table--sticky-header thead th {
  position: sticky;
  top: 0;
  z-index: 10;
}
</style>
